import { FunctionComponent, ReactNode } from 'react'
import { Theme } from '@emotion/react'
import styled from '@emotion/styled'
import { Button, Grid } from '@/tbui'
import { ChevronLeft, ChevronRight, DoubleChevronLeft, DoubleChevronRight } from './PaginationIcons'

const StyledGrid = styled(Grid)`
  border: 1px solid ${(props) => props.theme.palette.GRAY_2};
  border-radius: ${(props) => props.theme.shape.RADIUS};
  grid-template-rows: 1fr;
`

const StyledButton = styled(Button)<{ isActivePage?: boolean }>`
  background: ${({ isActivePage, theme }) =>
    isActivePage ? theme.palette.SECONDARY : 'transparent'};
  font-family: ${(props) => props.theme.font.FAMILY};
  color: ${(props) => props.theme.palette.PRIMARY};
  place-content: center;
  border-radius: 0;
  font-size: 1rem;
  min-width: 2rem;
  display: grid;
  height: 2rem;
  padding: 0;
  &:not(:first-of-type) {
    border-left: 1px solid ${(props) => props.theme.palette.GRAY_2};
  }
  &:hover {
    background: ${(props) => props.theme.palette.GRAY_1};
  }
`

export interface PaginationProps {
  onChange: (newPage: number) => void
  itemsPerPage: number
  currentPage: number
  total: number
}

const Pagination: FunctionComponent<PaginationProps> = ({
  itemsPerPage,
  currentPage,
  onChange,
  total,
}) => {
  const pages = Math.ceil(total / itemsPerPage)
  const lastPageIndex = pages - 1
  const isLastPage = currentPage >= lastPageIndex
  const isFirstPage = currentPage <= 0

  const getIconColor = (disabled: boolean): keyof Theme['palette'] => {
    return disabled ? 'GRAY_2' : 'GRAY_3'
  }

  const renderFirstPageButton = (): ReactNode => {
    const handleClick = (): void => onChange(0)

    return (
      <StyledButton data-test="arrow-button" disabled={isFirstPage} onClick={handleClick}>
        <DoubleChevronLeft color={getIconColor(isFirstPage)} />
      </StyledButton>
    )
  }

  const renderPrevPageButton = (): ReactNode => {
    const handleClick = (): void => onChange(currentPage - 1)

    return (
      <StyledButton data-test="arrow-button" disabled={isFirstPage} onClick={handleClick}>
        <ChevronLeft color={getIconColor(isFirstPage)} />
      </StyledButton>
    )
  }

  const renderPageButtons = (): ReactNode => {
    const renderPageButton = (p: undefined, index: number): ReactNode => {
      const isActivePage = index === currentPage
      const handleClick = (): void => onChange(index)
      return (
        <StyledButton
          data-test="page-button"
          isActivePage={isActivePage}
          disabled={isActivePage}
          onClick={handleClick}
          key={index}
        >
          {index + 1}
        </StyledButton>
      )
    }

    return [...Array(pages)].map(renderPageButton)
  }

  const renderNextPageButton = (): ReactNode => {
    const handleClick = (): void => onChange(currentPage + 1)

    return (
      <StyledButton data-test="arrow-button" onClick={handleClick} disabled={isLastPage}>
        <ChevronRight color={getIconColor(isLastPage)} />
      </StyledButton>
    )
  }

  const renderLastPageButton = (): ReactNode => {
    const handleClick = (): void => onChange(lastPageIndex)

    return (
      <StyledButton data-test="arrow-button" onClick={handleClick} disabled={isLastPage}>
        <DoubleChevronRight color={getIconColor(isLastPage)} />
      </StyledButton>
    )
  }

  return (
    <StyledGrid template={`repeat(${pages + 4}, auto)`} align="center" key="pagination">
      {renderFirstPageButton()}
      {renderPrevPageButton()}
      {renderPageButtons()}
      {renderNextPageButton()}
      {renderLastPageButton()}
    </StyledGrid>
  )
}

export default Pagination
